import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import addDays from "@insite/client-framework/Common/Utilities/addDays";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import setNotes from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetNotes";
import setRequestedDeliveryDate from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetRequestedDeliveryDate";
import setRequestedPickUpDate from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetRequestedPickUpDate";
import translate from "@insite/client-framework/Translate";
import { ShipViaDto } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Radio, { RadioPresentationProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupPresentationProps } from "@insite/mobius/RadioGroup";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import { InputStyle } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { addShippingInfoDataLayer } from "@sunSource/Components/AddToShippingInfoDataLayer";
import setOnePageCreateAccountData from "@sunSource/Handlers/NewOnePageCheckout/OnePageCheckoutCreateAccountHandler";
import CustomApplicationState from "@sunSource/Store/CustomApplicationState";
import { getEditedNotes } from "@sunSource/Store/CustomSelectors";
import { getOnePageCheckoutAccountState } from "@sunSource/Store/Data/NewOnePageCheckout/OnePageCheckoutStateSelectors";
import setShippingMethod from "@sunSource/Store/Pages/NewOnePageCheckout/Handlers/SetShippingMethod";
import flatMap from "lodash/flatMap";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch, useSelector } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState & CustomApplicationState) => {
    const {
        accountSettings: { enableWarehousePickup, allowEmptyShipping },
        cartSettings: { canRequestDeliveryDate, enableRequestPickUpDate, maximumDeliveryPeriod },
    } = getSettingsCollection(state);
    const { fulfillmentMethod, pickUpWarehouse } = state.context.session;
    const { cartId } = state.pages.checkoutShipping;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const { value: cart, isLoading: isCartLoading } = cartState;
    const editedCartNotes = getEditedNotes(state);

    const cartNotes = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    return {
        cart,
        isCartLoading,
        showShippingMethod: fulfillmentMethod === FulfillmentMethod.Ship,
        showRequestPickUpDate:
            enableWarehousePickup && enableRequestPickUpDate && fulfillmentMethod === FulfillmentMethod.PickUp,
        showRequestDeliveryDate: canRequestDeliveryDate,
        maximumDeliveryPeriod,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        pickUpWarehouse,
        notes: editedCartNotes ?? cartNotes?.notes,
        allowEmptyShipping,
        session: state.context.session,
        createAccountData: getOnePageCheckoutAccountState(state),
    };
};

const mapDispatchToProps = {
    setNotes,
    setShippingMethod,
    setRequestedDeliveryDate,
    setRequestedPickUpDate,
    addShippingInfoDataLayer,
    setOnePageCreateAccountData,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OnePageCheckoutDeliveryMethodStyles {
    textArea?: TextAreaProps;
    titleTextWrapper?: TypographyProps;
    titleTextHeading?: TypographyProps;
    container?: GridContainerProps;
    centeringWrapper?: InjectableCss;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    editGridItem?: GridItemProps;
    editLink?: LinkPresentationProps;
    fulfillmentMethodGridItem?: GridItemProps;
    fulfillmentMethodText?: TypographyPresentationProps;
    noCarriersFoundGridItem?: GridItemProps;
    noCarriersFoundText?: TypographyPresentationProps;
    servicesGridItem?: GridItemProps;
    notesGridItem?: GridItemProps;
    servicesRadioGroup?: RadioGroupPresentationProps;
    serviceRadio?: RadioPresentationProps;
    deliveryDateGridItem?: GridItemProps;
    deliveryDatePicker?: DatePickerPresentationProps;
    pickUpDateGridItem?: GridItemProps;
    pickUpDatePicker?: DatePickerPresentationProps;
    companyDetailsWrapper?: InjectableCss;
    companyName?: InjectableCss;
    contactInfo?: InjectableCss;
    changeLabel?: InjectableCss;
    companyDetailsGridItem?: GridItemProps;
    mainGridContainer?: GridContainerProps;
    inputHeader?: InjectableCss;
}

export const onePageCheckoutDeliveryMethodStyles: OnePageCheckoutDeliveryMethodStyles = {
    container: {
        gap: 20,
        css: css`
            margin-top: 30px;
        `,
    },
    textArea: {
        css: css`
            min-width: 100%;
            max-width: 100%;
            min-height: 50px;
            font-size: 14px !important;
            :focus {
                border: 2px solid ${getColor("primary")} !important;
            }
        `,
        labelProps: {
            color: "secondary",
            size: 14,
        },
    },
    titleTextWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 10px;
            > ${TypographyStyle} {
                &:last-child {
                    font-size: 14px;
                }
            }
        `,
    },
    titleTextHeading: {
        css: css`
            font-size: 18px !important;
        `,
    },
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 450px;
        `,
    },
    titleGridItem: {
        width: 10,
        css: css`
            align-items: center;
        `,
    },
    titleText: {
        variant: "h5",
        transform: "capitalize",
        css: css`
            margin-bottom: 0;
        `,
    },
    editGridItem: {
        width: 2,
        css: css`
            justify-content: end;
            align-items: center;
        `,
    },
    fulfillmentMethodGridItem: { width: 12 },
    fulfillmentMethodText: {
        variant: "h6",
        css: css`
            margin-bottom: 0;
        `,
    },
    noCarriersFoundGridItem: { width: 12 },
    servicesGridItem: {
        width: 12,
        css: css`
            padding-top: 0px;
        `,
    },
    notesGridItem: {
        width: 12,
        css: css`
            padding: 10px;
        `,
    },
    deliveryDateGridItem: { width: 12 },
    pickUpDateGridItem: { width: 12 },
    mainGridContainer: {
        css: css`
            padding: 0px 15px;
            [class^="GridItemStyle"] {
                padding-bottom: 5px;
                margin-bottom: 0;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding: 0px 12px;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ])}
            }
            [class^="GridOffset"] {
                margin-top: 15px;
                margin-bottom: 0;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding: 0px 12px;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ])}
            }
            .containerTitle {
                width: 100%;
                display: flex;
                margin: 0px 15px;
                > ${TypographyStyle} {
                    &:first-child {
                        font-weight: bold;
                        font-size: 18px;
                    }
                }
                [class^="CheckboxGroupStyle"] {
                    margin-left: 30px;
                }
            }
            [class^="FormInputWrapper"] {
                margin-top: 0 !important;
                > ${InputStyle} {
                    :focus {
                        border: 2px solid ${getColor("primary")} !important;
                    }
                }
                > select[class^="SelectStyle"] {
                    :focus {
                        border: 2px solid ${getColor("primary")} !important;
                    }
                }
            }
        `,
    },
    companyDetailsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    companyName: {
        css: css`
            font-weight: 600;
        `,
    },
    contactInfo: {
        css: css`
            color: gray;
        `,
    },
    changeLabel: {
        css: css`
            color: #00539b;
            fontweight: 600;
            cursor: pointer;
        `,
    },
    companyDetailsGridItem: {
        width: 6,
        css: css`
            paddingtop: 0px;
        `,
    },
    inputHeader: {
        css: css`
            display: block;
            font-size: 18px !important;
            padding-left: 15px;
        `,
    },
    servicesRadioGroup: {
        css: css`
            label {
                font-size: 14px !important;
            }
        `,
    },
};

const styles = onePageCheckoutDeliveryMethodStyles;

interface State {
    notes?: string;
}

const OnePageCheckoutDeliveryMethod = ({
    cart,
    isCartLoading,
    cartUrl,
    pickUpWarehouse,
    showShippingMethod,
    showRequestPickUpDate,
    showRequestDeliveryDate,
    maximumDeliveryPeriod,
    setRequestedDeliveryDate,
    setRequestedPickUpDate,
    setShippingMethod,
    setNotes,
    allowEmptyShipping,
    session,
    setOnePageCreateAccountData,
    createAccountData,
}: Props) => {
    const [selectedShipVia, setSelectedShipVia] = useState<ShipViaDto | null>(null);
    const [isUpdatingCart, setIsUpdatingCart] = useState(false);
    const [notes, setLocalNotes] = useState("");

    const [changeDeliveryMethodClicked, setChangeDeliveryMethodClicked] = useState(false);
    const [deliveryMethod, setDeliveryMethod] = useState("");

    // const editedCartNotes = useSelector(getEditedNotes);

    const cartId = useSelector((state: ApplicationState) => state.pages.checkoutShipping.cartId);

    // const carter = cartId
    //     ? useSelector((state: any) => getCartState(state, cartId).value)
    //     : useSelector(getCurrentCartState).value;

    const carter = cartId
        ? useSelector((state: ApplicationState) => getCartState(state, cartId).value)
        : useSelector((state: ApplicationState) => getCurrentCartState(state).value);

    const currentNotes = carter?.notes;

    const dispatch = useDispatch();

    // useEffect(() => {
    //     setLocalNotes(editedCartNotes ?? carter?.notes ?? "");
    // }, [editedCartNotes, carter?.notes]);

    // const notesChangeHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
    //     const updatedNotes = event.currentTarget.value;
    //     setLocalNotes(updatedNotes);
    //     dispatch(setNotes({ notes: updatedNotes }));
    // };

    useEffect(() => {
        if (typeof notes === "undefined" && typeof currentNotes !== "undefined") {
            setLocalNotes(currentNotes);
        }
    }, [notes, currentNotes]);

    const notesChangeHandler = (value: string) => {
        if (session.isAuthenticated) {
            setLocalNotes(value);
            setNotes({ notes: value });
        } else {
            setLocalNotes(value);
            localStorage.removeItem("orderNotes");
            localStorage.setItem("orderNotes", value); // Store in localStorage
            setOnePageCreateAccountData({
                ...createAccountData,
                tempoData: { ...createAccountData.tempoData, orderNotes: value },
            });
        }
    };

    const serviceChangeHandler = (event: string) => {
        if (!cart?.carriers) {
            return;
        }
        if (session.isAuthenticated) {
            const shipVias = flatMap(cart.carriers, o => o.shipVias).filter(o => !!o) as ShipViaDto[];
            const shipVia = shipVias.find(o => o.id === event);
            if (!shipVia) {
                return;
            }

            const carrier = cart.carriers.find(o => o.shipVias?.some(p => p.id === shipVia.id));
            if (!carrier) {
                return;
            }

            setIsUpdatingCart(true);
            setSelectedShipVia(shipVia);
            const currencyCode = session.currency?.currencyCode;
            if (cart) {
                if (cart.cartLines && cart.carrier && cart.carrier.description) {
                    addShippingInfoDataLayer(
                        cart.cartLines,
                        shipVia.description,
                        carter?.orderGrandTotal ?? "",
                        currencyCode,
                    );
                }
            }
            setShippingMethod({ carrier, shipVia });
        } else {
            localStorage.removeItem("serviceValue");
            localStorage.setItem("serviceValue", event); // Store in localStorage
            setOnePageCreateAccountData({
                ...createAccountData,
                tempoData: { ...createAccountData.tempoData, savedService: event },
            });
            setSelectedShipVia({ description: "", id: event, isDefault: true });
        }
    };

    useEffect(() => {
        if (cart) {
            setSelectedShipVia(cart.shipVia);
            setIsUpdatingCart(false);
        }
    }, [cart?.carrier, cart?.shipVia]);

    useEffect(() => {
        const savedService = localStorage.getItem("serviceValue");
        const orderNotes = localStorage.getItem("orderNotes");
        if (session.isAuthenticated) {
            if (savedService !== null) {
                serviceChangeHandler(savedService);
            }
            if (orderNotes !== null) {
                notesChangeHandler(orderNotes);
            }
        }
    }, [session.isAuthenticated]);

    useEffect(() => {
        if (session.isAuthenticated) {
            const savedService = localStorage.getItem("serviceValue");
            flatMap(
                cart?.carriers,
                carrier => carrier.shipVias?.map(shipVia => ({ ...shipVia, carrier })),
                // eslint-disable-next-line array-callback-return
            ).map(shipVia => {
                if (shipVia?.id === savedService) {
                    setDeliveryMethod(shipVia.description);
                }
            });
        }
    }, [session.isAuthenticated]);

    if (!cart || (cart.lineCount && (!cart.cartLines || cart.cartLines.length === 0)) || !cart.carriers) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner data-test-selector="onePageCheckout_deliveryMethodLoading" />
            </StyledWrapper>
        );
    }

    const handleRequestPickUpDateChanged = ({ selectedDay, selectedDayDisabled }: DatePickerState) => {
        setRequestedPickUpDate({
            requestedPickUpDate: selectedDay,
            requestedPickUpDateDisabled: selectedDayDisabled,
        });
    };
    const handleRequestDeliveryDateChanged = ({ selectedDay, selectedDayDisabled }: DatePickerState) => {
        setRequestedDeliveryDate({
            requestedDeliveryDate: selectedDay,
            requestedDeliveryDateDisabled: selectedDayDisabled,
        });
    };

    if (!cart?.carriers.length) {
        return <></>;
    }
    // if (session.isAuthenticated && !changeDeliveryMethodClicked) {
    //     return (
    //         <StyledWrapper>
    //             <GridContainer {...styles.mainGridContainer}>
    //                 <GridItem>
    //                     <StyledWrapper {...styles.companyDetailsWrapper}>
    //                         <Typography {...styles.companyName}>
    //                             {deliveryMethod}
    //                             <Typography
    //                                 onClick={() => {
    //                                     setChangeDeliveryMethodClicked(true);
    //                                 }}
    //                                 {...styles.changeLabel}
    //                             >
    //                                 (Change)
    //                             </Typography>
    //                         </Typography>
    //                     </StyledWrapper>
    //                 </GridItem>
    //             </GridContainer>
    //         </StyledWrapper>
    //     );
    // }
    if (session.isAuthenticated) {
        return null;
    }
    return (
        <GridContainer {...styles.container}>
            <Typography {...styles.inputHeader}>{translate("Delivery method *")}</Typography>
            {!session.isAuthenticated && (
                <>
                    {cart.carriers?.length === 0 && !allowEmptyShipping && (
                        <GridItem {...styles.noCarriersFoundGridItem}>
                            <Typography {...styles.noCarriersFoundText}>
                                {siteMessage("ReviewAndPay_NoCarriersFound")}
                            </Typography>
                        </GridItem>
                    )}
                    {cart.carriers.length > 0 && (
                        <GridItem {...styles.servicesGridItem}>
                            <RadioGroup
                                {...styles.servicesRadioGroup}
                                value={selectedShipVia?.id}
                                disabled={isCartLoading || isUpdatingCart}
                                onChangeHandler={e => serviceChangeHandler(e.currentTarget.value)}
                                data-test-selector="onePageCheckout_chooseDeliveryMethod"
                            >
                                {flatMap(cart.carriers, carrier =>
                                    carrier.shipVias?.map(shipVia => ({ ...shipVia, carrier })),
                                ).map(shipVia => (
                                    <Radio
                                        {...styles.serviceRadio}
                                        key={shipVia?.id}
                                        value={shipVia?.id}
                                        data-test-selector={`${shipVia?.carrier.description} - ${shipVia?.description}`}
                                    >
                                        {shipVia?.carrier.description} - {shipVia?.description}
                                    </Radio>
                                ))}
                            </RadioGroup>
                        </GridItem>
                    )}
                    <GridItem {...styles.notesGridItem}>
                        <TextArea
                            {...styles.textArea}
                            placeholder={translate("Please provide additional shipper details")}
                            value={notes}
                            onChange={e => notesChangeHandler(e.currentTarget.value)}
                        />
                    </GridItem>
                </>
            )}
            {showShippingMethod && session.isAuthenticated && (
                <>
                    {cart.carriers?.length === 0 && !allowEmptyShipping && (
                        <GridItem {...styles.noCarriersFoundGridItem}>
                            <Typography {...styles.noCarriersFoundText}>
                                {siteMessage("ReviewAndPay_NoCarriersFound")}
                            </Typography>
                        </GridItem>
                    )}
                    {cart.carriers.length > 0 && (
                        <GridItem {...styles.servicesGridItem}>
                            <RadioGroup
                                {...styles.servicesRadioGroup}
                                value={selectedShipVia?.id}
                                disabled={isCartLoading || isUpdatingCart}
                                onChangeHandler={e => serviceChangeHandler(e.currentTarget.value)}
                                data-test-selector="onePageCheckout_chooseDeliveryMethod"
                            >
                                {flatMap(cart.carriers, carrier =>
                                    carrier.shipVias?.map(shipVia => ({ ...shipVia, carrier })),
                                ).map(shipVia => (
                                    <Radio
                                        {...styles.serviceRadio}
                                        key={shipVia?.id}
                                        value={shipVia?.id}
                                        data-test-selector={`${shipVia?.carrier.description} - ${shipVia?.description}`}
                                    >
                                        {shipVia?.carrier.description} - {shipVia?.description}
                                    </Radio>
                                ))}
                            </RadioGroup>
                        </GridItem>
                    )}
                    <StyledWrapper {...styles.notesGridItem}>
                        <StyledWrapper {...styles.titleTextWrapper}>
                            <Typography {...styles.titleTextHeading}>{translate("Shipping Notes:")}</Typography>
                            <Typography>
                                {translate(
                                    "Please specify any special shipping instructions, preferred shipping account details, or priority shipping needs.",
                                )}
                            </Typography>
                        </StyledWrapper>
                        <TextArea
                            {...styles.textArea}
                            placeholder={translate("Please provide Shipping Notes here")}
                            value={notes}
                            onChange={e => notesChangeHandler(e.currentTarget.value)}
                        />
                    </StyledWrapper>
                    {showRequestDeliveryDate && (
                        <GridItem
                            {...styles.deliveryDateGridItem}
                            data-test-selector="onePageCheckout_requestedDeliveryDate"
                        >
                            <DatePicker
                                {...styles.deliveryDatePicker}
                                label={`${translate("Request Delivery Date")} (${translate("optional")})`}
                                hint={siteMessage("Checkout_RequestedDeliveryDateInformation")}
                                selectedDay={cart.requestedDeliveryDateDisplay!}
                                dateTimePickerProps={{
                                    minDate: new Date(),
                                    maxDate: addDays(new Date(), maximumDeliveryPeriod),
                                    monthAriaLabel: translate("Request delivery date month"),
                                    dayAriaLabel: translate("Request delivery date day"),
                                    yearAriaLabel: translate("Request delivery date year"),
                                    ...styles.deliveryDatePicker?.dateTimePickerProps,
                                }}
                                onDayChange={handleRequestDeliveryDateChanged}
                            />
                        </GridItem>
                    )}
                </>
            )}
            {showRequestPickUpDate && (
                <GridItem {...styles.pickUpDateGridItem} data-test-selector="onePageCheckout_requestPickUpDate">
                    <DatePicker
                        {...styles.pickUpDatePicker}
                        label={`${translate("Request Pick Up Date")} (${translate("optional")})`}
                        hint={siteMessage("Checkout_RequestedPickupDateInformation")}
                        selectedDay={cart.requestedPickupDateDisplay!}
                        dateTimePickerProps={{
                            minDate: new Date(),
                            maxDate: addDays(new Date(), maximumDeliveryPeriod),
                            ...styles.pickUpDatePicker?.dateTimePickerProps,
                        }}
                        onDayChange={handleRequestPickUpDateChanged}
                        monthAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                            "Enter month, format M M",
                        )}`}
                        dayAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                            "Enter day, format D D",
                        )}`}
                        yearAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                            "Enter year, format Y Y Y Y",
                        )}`}
                    />
                </GridItem>
            )}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OnePageCheckoutDeliveryMethod),
    definition: {
        displayName: "New One page checkout Delivery Method",
        group: "One Page Checkout",
        // allowedContexts: ["OnePageCheckoutPage"],
    },
};

export default widgetModule;
